import React, {ChangeEvent, memo, useCallback, useEffect, useState} from 'react';
import PageBlock from "../../../components/PageBlock";
import {Button, CardBody, Checkbox} from "@material-tailwind/react";
import {API} from "../../../types/API";
import {AxiosResponse} from "axios";
import {Input} from "@material-tailwind/react/components/Input";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../../store";
import {getError, hasError} from "../../../hooks/errors";
import {clear, validateData} from "../../../reducers/validationReducer";
import {InputType} from "../../../types/InputType";
import {useToastContext} from "../../../types/contexts/ToastContext";
import {useNavigate, useParams} from "react-router-dom";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import FormSkeleton from "../../../components/Skeletons/FormSkeleton";
import {ErrorHandler} from "../../../hooks/errorHandler";
import {useErrorHandler} from "react-error-boundary";
import {useUserContext} from "../../../types/contexts/UserContext";
import Forbidden from "../../../components/Forbidden";
import {useTranslation} from "react-i18next";
import Card from "../../../components/Card";
import {setTitle} from "../../../hooks/setTitle";
import {Source} from "../../../types/Responses/SourcesResponse";
import {APILink} from "../../../types/APILink";
import {checkEnabled2FA} from "../../../hooks/checkEnabled2FA";
import {removeDataTableRequest} from "../../../reducers/dataTableSearch";
import {setSidebarTitle} from "../../../reducers/sidebar";

const SourceForm = () => {
    const {t} = useTranslation();
    const {id} = useParams();
    const navigate = useNavigate();
    const toast = useToastContext();
    const handleError = useErrorHandler();
    const user = useUserContext();
    const [source, setSource] = useState<string>();
    const [openInNewTab, setOpenInNewTab] = useState<boolean>();
    const [isInProgress, setInProgress] = useState<boolean>(false);
    const [validated, setValidated] = useState<boolean>(false);
    const [formIsReady, setFormIsReady] = useState<boolean>(false);
    const [isForbidden, setIsForbidden] = useState<boolean>(false);

    const dispatch = useDispatch();
    const errors = useSelector((state: RootState) => state.validation.errors);

    const changeSource = (e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setSource(e.currentTarget.value)
        }
    }

    const onNewTabChange = useCallback((e: ChangeEvent<HTMLInputElement> | undefined) => {
        if (e && e.target) {
            setOpenInNewTab(parseInt(e.target.value) === 1);
        }
    }, [openInNewTab, setOpenInNewTab]);

    const handleSuccessResponse = (response: AxiosResponse, message: JSX.Element | string) => {
        const source = response.data.data as Source
        if (source) {
            toast?.show(<><FontAwesomeIcon icon={solid("circle-check")}
                                           className="mr-4"/> {message}</>, "bg-teal-500");
            navigate('/app/admin/sources');
        }
    }

    const callback = (b: boolean) => {
        setValidated(true);
        setInProgress(false);

        if (b) {
            const request = {source, open_in_new_tab: openInNewTab};
            if (id) {
                API.updateSource(parseInt(id ?? ''), request).then((response: AxiosResponse) => handleSuccessResponse(response, t('admin.sources.updated.title', {name: source}))).catch(e => ErrorHandler(e, `put${APILink.GET_SOURCE.replace('$source', id.toString())}`, toast, handleError));
            } else {
                API.createSource(request).then((response: AxiosResponse) => {
                    dispatch(removeDataTableRequest('/app/admin/sources'));
                    handleSuccessResponse(response, t('admin.sources.created.title', {name: source}));
                }).catch(e => ErrorHandler(e, `post${APILink.GET_SOURCES}`, toast, handleError));
            }

        }
    }

    const submit = () => {
        setInProgress(true);
        dispatch(validateData({
            payload: {
                inputs: [
                    {key: 'source', label: t('admin.sources.table.source'), type: InputType.text, regex: '^.{3,}\\b$'},
                ],
                introducedData: {source},
                callback: callback
            }
        }));
    }
    const getSingleSource = useCallback(() => {
        API.getSource(parseInt(id ?? '')).then((response: AxiosResponse) => {
            const sourceResponse = response.data.data as Source
            setSource(sourceResponse.source);
            setOpenInNewTab(sourceResponse.open_in_new_tab);
            setFormIsReady(true);
            setTitle(t('admin.buttons.update') + " " + sourceResponse.source);
            dispatch(setSidebarTitle(t('admin.sources.update.title', {name: sourceResponse.source})));
        }).catch(e => {
            ErrorHandler(e, `get${APILink.GET_SOURCE.replace('$source', id?.toString() ?? '')}`, toast, handleError, setIsForbidden)
            setFormIsReady(true);
        });
    }, [setSource, setFormIsReady, toast, handleError, id]);

    useEffect(() => {
        if (user && user.user){
            checkEnabled2FA(user.user).then(() => {
                if (id) {
                    getSingleSource();
                } else {
                    setTitle(t('admin.sources.create.title'));
                    dispatch(setSidebarTitle(t('admin.sources.create.title')));
                    setFormIsReady(true);
                    if (user && !user.user.permissions.includes('create source')) {
                        setIsForbidden(true);
                    }
                }
            }).catch()
        }
        dispatch(clear())
    }, [dispatch, id, user, t]);

    return (
        <PageBlock>
            {!formIsReady ? <FormSkeleton/> : <>
                <Card className="mt-6 bg-white dark:bg-blue-gray-900 dark:text-gray-500">
                    <CardBody className="overflow-auto px-0">
                        {!isForbidden && <>
                            <div className="max-w-md">
                                <Input success={!hasError('source', errors) && validated}
                                       error={validated && hasError('source', errors)} crossOrigin=''
                                       variant={'outlined'} label={t('admin.sources.table.source')} type={'text'}
                                       onChange={changeSource}
                                       value={source}
                                       className="inline-block dark:text-gray-500 dark:border-blue-gray-800 dark:placeholder-shown:border-blue-gray-800 dark:focus:border-blue-gray-800 dark:focus:border-t-transparent"
                                       labelProps={{className: "dark:after:border-blue-gray-800 dark:before:border-blue-gray-800 dark:peer-focus:text-white dark:peer-focus:before:!border-blue-gray-800 dark:peer-focus:after:!border-blue-gray-800"}}/>
                                {validated && hasError('source', errors) &&
                                    <label key={'error-source'}
                                           className="mt-1 text-red-500 text-xs block">{getError('source', errors)}</label>}
                            </div>
                            <div className="max-w-md mt-4">
                                <Checkbox checked={openInNewTab}
                                          onChange={onNewTabChange}
                                          color={'teal'} crossOrigin={''}
                                          value={openInNewTab ? 0 : 1} label={<span
                                    className="dark:text-gray-500">{t("admin.sources.table.open_in_new_tab")}</span>}/>
                            </div>

                            <div className="mt-12">
                                <Button onClick={() => submit()} className="text-white bg-teal-500"
                                        disabled={isInProgress}>{id ? t('admin.buttons.update') : t('admin.buttons.create')}</Button>
                            </div>
                        </>}
                        <Forbidden isForbidden={isForbidden}/>
                    </CardBody>
                </Card>
            </>}
        </PageBlock>
    );
}
export default memo(SourceForm);
